import { createDecorator } from "../../instantiation/common/registry.js";
import type { Event } from "../../../base/common/events.js";

export const ICodeViewService = createDecorator<ICodeViewService>("codeViewService");

/**
 * One selectable source of code for the current selection — e.g. an object's
 * class source, a component's source, a material's source, or a shader stage.
 * Contributors (currently the three.js property walk — see the TEMPORARY hook in
 * `propertyProvider.ts`) report these; the code panel renders them as a dropdown.
 */
export interface CodeOption {
    /** Stable id within the current option set (used for selection). */
    id: string;
    /**
     * The ROLE of this option, stable across selections — e.g. `"fragment-shader"`,
     * `"vertex-shader"`, `"material"`, `"component"`, `"object"`. Used to keep the
     * same *kind* of code shown when the selection changes (select a fragment
     * shader, pick another object with one → its fragment shader shows).
     */
    kind: string;
    /** Short label for the dropdown (e.g. "Fragment Shader"). */
    label: string;
    /** Optional grouping label — the owning object/component/material name. */
    group?: string;
    /**
     * What KIND of owner this code belongs to — for analytics and generic
     * consumers ("did they read material code or component code?").
     */
    category?: "object" | "material" | "component" | "function";
    /**
     * The owner's class/type name (e.g. "MeshStandardNodeMaterial", "Mesh",
     * a component class) — a type identifier, never a user-authored instance
     * name.
     */
    ownerType?: string;
    /** Highlighting language hint for the viewer (e.g. "glsl"); default JS/TS. */
    language?: string;
    /** The produced text is already final — skip the viewer's minified-reformat
     *  step. Required when {@link caret} offsets must stay valid (reformatting
     *  would shift them). */
    preformatted?: boolean;
    /** Optional caret/selection to reveal on open, as CHAR OFFSETS into the produced
     *  text (the viewer converts to line:col). E.g. the slow-frame position. */
    caret?: { start: number; end: number };
    /** This code IS a project file's content, and the file is EDITABLE through
     *  the OT text rail (file-identity plan, step 5): the code panel flips
     *  read-only off and routes edits through ITextFileSyncService. Absent =
     *  view-only (generated/compiled/foreign sources). */
    file?: { path: string };
    /** Produces the code string (may be async). Null when unavailable. */
    produce: () => string | null | Promise<string | null>;
}

/**
 * The code panel's option registry, driven entirely by the inspection service's
 * target layers ({@link IInspectionService.getTargets}): selection, Resources
 * clicks, node-graph drill-downs and clicked function nodes all arrive through
 * that one bus — this service holds NO follow/override policy of its own, only
 * the per-panel lock and visibility. It collects the options contributors
 * report for the current targets (targets may also carry explicit options in
 * `payloads.code`, e.g. a TSL Fn body) and keeps the same {@link CodeOption.kind}
 * selected across changes. Coupling is soft: contributors resolve this service
 * optionally, so products without a code panel simply don't register it.
 */
export interface ICodeViewService {
    readonly _serviceBrand: undefined;
    /** Fires (as a signal) whenever options/selection/lock change; read the getters after. */
    readonly onDidChange: Event<void>;
    /** The code options available for the current selection. */
    readonly options: readonly CodeOption[];
    /** The selected option (sticky by {@link CodeOption.kind}), or null. */
    readonly selected: CodeOption | null;
    /** Select an option by id — also updates the sticky kind preference. */
    select(id: string): void;
    /** Lock on = freeze the current options, ignoring selection changes. */
    readonly locked: boolean;
    toggleLock(): void;
    /**
     * Report whether a view is showing the code (the panel's tab visibility).
     * While hidden the service does not trigger the (potentially expensive)
     * property walk; becoming visible re-collects the current selection.
     */
    setConsumerVisible(visible: boolean): void;
    /**
     * Contributors report the code options they found while walking `target`.
     * Replaces the options for `target`; ignored when `target` isn't the current
     * selection (a stray walk can't clobber it). Fires {@link onDidChange}.
     */
    reportOptions(target: object, options: CodeOption[]): void;

    /**
     * Show a one-off, selection-INDEPENDENT snippet (e.g. a section of the script
     * behind a slow frame). It replaces the current options until the next real
     * selection change dismisses it, restoring the normal inspection-driven view.
     * Not tied to any inspection target — the caller supplies the code directly.
     */
    showEphemeral(option: CodeOption): void;
}
